import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { conversationsApi } from '@/api/endpoints'
import { ErrorNote, Loading } from '@/components/common/Ui'
import { ORDER_STATUS_LABELS, dateTime, relative } from '@/lib/format'
import { DATE_RANGES } from '@/lib/dateRanges'
import { useMediaUrl } from '@/lib/useMediaUrl'
import Icon from '@/components/common/Icons'

const ICONS = { text: '💬', audio: '🎤', image: '📷', document: '📄', interactive: '🔘' }

const initials = (name) =>
  (name || '?')
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase()

/**
 * Conversations, one per order.
 *
 * An order arrived needing a decision and the reason sat in a WhatsApp
 * thread on somebody's phone — every fault found in that flow was diagnosed
 * from a screenshot somebody sent.
 *
 * A conversation is an order rather than a customer: a customer has one
 * continuous thread, and no shop reads a year of it to judge today's order.
 */
export default function Conversations() {
  // Two values, on purpose. `search` is what the box shows and updates on
  // every keystroke, so typing never feels held back. `query` is what the
  // server is asked about, and it lags behind — otherwise 'dhamo' was five
  // searches, four of them thrown away before they landed.
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [dates, setDates] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [openKey, setOpenKey] = useState(null)

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search), 300)
    return () => clearTimeout(timer)
  }, [search])

  // A preset is shorthand for a from/to pair; everything downstream sees
  // plain dates, the same as the Orders screen.
  const range =
    dates === 'custom'
      ? {
          ...(from ? { date_from: from } : {}),
          ...(to ? { date_to: to } : {}),
        }
      : (DATE_RANGES[dates] ?? DATE_RANGES['']).range()
  const params = { ...(query ? { search: query } : {}), ...range }
  const narrowed = Boolean(query || dates)

  const list = useQuery({
    queryKey: ['conversations', params],
    queryFn: () => conversationsApi.list(params),
    refetchInterval: 20_000,
    // Keep showing the previous matches while the next ones load. Without
    // it a new search key has no data, the component below returns
    // <Loading /> and the whole pane — search box included — is unmounted
    // and rebuilt, which is where the focus went.
    placeholderData: (previous) => previous,
  })

  const thread = useQuery({
    queryKey: ['conversation', openKey],
    queryFn: () => conversationsApi.get(openKey),
    enabled: Boolean(openKey),
    refetchInterval: 20_000,
  })

  // Only before there is anything at all to show. Once the pane is on
  // screen it stays on screen; a search refreshes the rows inside it.
  if (list.isLoading && !list.data) return <Loading />
  if (list.isError && !list.data) {
    return <ErrorNote message="Could not load conversations" onRetry={list.refetch} />
  }

  const rows = list.data ?? []
  const current = openKey ? rows.find((r) => r.key === openKey) : null

  return (
    <div className="inbox">
      <aside className="inbox-list">
        <div className="inbox-head">
          <strong>Conversations</strong>
          <span className="hint">
            {rows.length}
            {narrowed ? ' matching' : ' in the last while'}
          </span>
          <input
            type="search"
            placeholder="Name, phone or order number…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="inbox-filters">
            <select value={dates} onChange={(e) => setDates(e.target.value)}>
              {Object.entries(DATE_RANGES).map(([value, { label }]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            {narrowed && (
              <button
                type="button"
                className="btn btn-sm btn-icon"
                title="Clear all filters"
                aria-label="Clear all filters"
                onClick={() => {
                  setSearch('')
                  setQuery('')
                  setDates('')
                  setFrom('')
                  setTo('')
                }}
              >
                <Icon name="clearFilter" />
              </button>
            )}
          </div>

          {/* Stacked, not side by side: at this width two date inputs on one
              line squeeze each other down to unreadable. */}
          {dates === 'custom' && (
            <div className="inbox-dates">
              <label className="field">
                <span>From</span>
                <input
                  type="date"
                  value={from}
                  max={to || undefined}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </label>
              <label className="field">
                <span>To</span>
                <input
                  type="date"
                  value={to}
                  min={from || undefined}
                  onChange={(e) => setTo(e.target.value)}
                />
              </label>
            </div>
          )}
        </div>

        {rows.length === 0 && <p className="hint pad">Nothing to show.</p>}

        <ul>
          {rows.map((row) => (
            <li key={row.key}>
              <button
                type="button"
                className={`inbox-row${row.key === openKey ? ' active' : ''}`}
                onClick={() => setOpenKey(row.key)}
              >
                <span className="avatar">{initials(row.name)}</span>
                <span className="inbox-row-body">
                  <span className="inbox-row-top">
                    <strong>{row.name}</strong>
                    <span className={`badge ${row.is_open ? 'tier' : ''}`}>
                      {row.is_open ? 'Active' : ORDER_STATUS_LABELS[row.status] ?? row.status}
                    </span>
                  </span>
                  <span className="mono hint">{row.phone}</span>
                  <span className="inbox-preview">
                    {row.last_from_us && 'Bot: '}
                    {row.last_text ?? '—'}
                  </span>
                  <span className="hint">{relative(row.last_at)}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <section className="inbox-thread">
        {!openKey && (
          <p className="hint pad">Pick a conversation to read it.</p>
        )}

        {openKey && thread.isLoading && <Loading />}

        {openKey && thread.data && (
          <>
            <header className="thread-head">
              <div>
                <strong>{thread.data.name}</strong>
                <span className="mono hint">{thread.data.phone}</span>
              </div>
              {thread.data.order_id && (
                <Link to={`/orders/${thread.data.order_id}`} className="btn btn-sm">
                  {thread.data.order_number}
                </Link>
              )}
            </header>

            <div className="thread">
              {thread.data.turns.length === 0 && (
                <p className="hint pad">No messages in this conversation.</p>
              )}
              {thread.data.turns.map((turn) => (
                <TurnBubble key={turn.id} turn={turn} />
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  )
}

/**
 * One message in the thread, with whatever the customer actually sent.
 *
 * The recording used to be missing entirely. The thread showed the
 * transcript and nothing else — reasonable while every voice note had one,
 * but a message we stopped short on has no transcript, so it rendered as
 * '(no text)' with no way to hear it and nothing saying why. That is the
 * case where hearing it matters most: an unregistered number the shop has
 * been told to ring back.
 */
function TurnBubble({ turn }) {
  const theirs = turn.direction === 'inbound'
  const { url, failed } = useMediaUrl(turn.id, turn.has_media)
  const text = turn.transcript || turn.body

  return (
    <div className={`turn ${theirs ? 'theirs' : 'ours'}`}>
      <div className="bubble">
        {theirs && turn.msg_type !== 'text' && (
          <div className="turn-kind">
            {ICONS[turn.msg_type] ?? '•'} {turn.msg_type}
          </div>
        )}

        {turn.msg_type === 'audio' && turn.has_media && (
          url ? (
            <audio controls src={url} preload="metadata" className="turn-audio" />
          ) : (
            <p className="turn-body muted">
              {failed ? 'Recording unavailable' : 'Loading recording…'}
            </p>
          )
        )}

        {turn.msg_type === 'image' && turn.has_media && url && (
          <a href={url} target="_blank" rel="noreferrer">
            <img className="turn-image" src={url} alt="What the customer sent" />
          </a>
        )}

        {/* The transcript where there is one: it is what the order was read
            from, and the line a reviewer has to judge. Silent rather than
            '(no text)' when the message is a recording we never transcribed
            — the player above already says what arrived. */}
        {text ? (
          <p className="turn-body">{text}</p>
        ) : (
          !turn.has_media && <p className="turn-body muted">(no text)</p>
        )}

        <div className="turn-foot">{dateTime(turn.at)}</div>
      </div>
    </div>
  )
}
